import { useState } from 'react'
import { DatasetDocumentCover } from '@/components/library/DatasetDocumentCover'
import Link from '@/routing/Link'
import { useTranslation } from 'react-i18next'
import { FileText } from '@digital-twins/design-system/icons'
import {
  isContextMaterialIndexingInProgress,
  isContextMaterialIndexingReady,
} from '@digital-twins/shared-types'
import { coverTone } from '@/components/library/LibraryItemCover'
import { Checkbox } from '@/components/ui/checkbox'
import { MarketLabel } from '@/components/ui/MarketLabel'
import { Text } from '@/components/ui/Text'
import { KnowledgeDocumentWithStages } from '@/lib/api'
import { KnowledgeLibrarySelection } from '@/types/components/knowledge-document-collection'
import { indexingStageLabel, KnowledgeIndexingBadge } from './KnowledgeIndexingBadge'
import { KnowledgeStageFailureNotice } from './KnowledgeStageFailureNotice'
import {
  documentCoverUrl,
  documentDescription,
  documentTitle,
  facetValue,
  fileKindLabel,
  marketDisplayName,
  marketFlagCode,
} from './libraryHelpers'

interface KnowledgeLibraryListViewProps {
  documents: Array<KnowledgeDocumentWithStages>
  onDocumentUpdated?: (doc: KnowledgeDocumentWithStages) => void
  /** Opens the document in the library's overlay pane instead of navigating to a new page. */
  onOpen?: (token: string) => void
  /** Absent: no checkboxes (unused until AF8, which draws a leading checkbox cell per row). */
  selection?: KnowledgeLibrarySelection
}

interface ListCoverThumbProps {
  doc: KnowledgeDocumentWithStages
}

/**
 * The row's cover in a 40 x 52 frame: the same source as the grid card, and
 * a token gradient with the document mark when the image cannot load, so a
 * row never shows a broken image.
 */
function ListCoverThumb(props: ListCoverThumbProps) {
  const { doc } = props

  const [hasError, setHasError] = useState(false)

  const coverSrc = documentCoverUrl(doc)
  const tone = coverTone(doc.token)
  const frameClass =
    'relative h-[52px] w-10 shrink-0 overflow-hidden rounded-md border border-card-border bg-primary/5'

  if (!coverSrc) {
    return (
      <span className={frameClass} aria-hidden="true">
        <DatasetDocumentCover />
      </span>
    )
  }

  if (hasError) {
    return (
      <span
        className={`${frameClass} flex items-center justify-center ${tone.wash}`}
        aria-hidden="true"
        data-testid={`knowledge-list-thumb-${doc.token}`}
        data-cover="generated"
      >
        <FileText className={`h-4 w-4 ${tone.ink}`} />
      </span>
    )
  }

  return (
    <span className={frameClass} aria-hidden="true">
      <img
        src={coverSrc}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover object-top"
        onError={() => setHasError(true)}
        data-testid={`knowledge-list-thumb-${doc.token}`}
      />
    </span>
  )
}

/**
 * The Library as a table: a cover thumbnail beside each title, then kind,
 * market and year. A missing market or year leaves its cell empty. A row's
 * indexing status shows under its title only while it is not ready.
 */
export function KnowledgeLibraryListView({
  documents,
  onDocumentUpdated,
  onOpen,
  selection,
}: KnowledgeLibraryListViewProps) {
  const { t } = useTranslation('app')
  return (
    <div
      className="overflow-hidden rounded-2xl border border-border bg-card-inset dark:bg-card"
      data-testid="knowledge-list-view"
    >
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40 text-left text-muted-foreground">
          <tr>
            {selection ? <th className="w-10 px-4 py-3" aria-hidden="true" /> : null}
            <th className="px-4 py-3 font-medium">{t('library.colTitle')}</th>
            <th className="hidden px-4 py-3 font-medium sm:table-cell">{t('library.colKind')}</th>
            <th className="hidden px-4 py-3 font-medium md:table-cell">{t('library.colMarket')}</th>
            <th className="hidden px-4 py-3 font-medium md:table-cell">{t('library.colYear')}</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc, index) => {
            const description = documentDescription(doc)
            const inProgress = isContextMaterialIndexingInProgress(
              doc.indexingStatus,
            )
            const market = facetValue(doc.facets, 'market')
            const year = facetValue(doc.facets, 'year')
            const isReady = isContextMaterialIndexingReady(doc.indexingStatus)
            return (
            <tr
              key={doc.token}
              className="border-b border-border last:border-0 hover:bg-muted/30"
              data-testid={`knowledge-list-row-${doc.token}`}
            >
              {selection ? (
                <td className="w-10 px-4 py-3">
                  <Checkbox
                    checked={selection.selectedTokens.has(doc.token)}
                    onCheckedChange={() => selection.onToggle(doc.token)}
                    aria-label={t('library.collections.selection.select', {
                      name: documentTitle(doc),
                    })}
                    data-testid={`knowledge-library-row-select-${index}`}
                  />
                </td>
              ) : null}
              <td className="px-4 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <ListCoverThumb doc={doc} />
                  <div className="min-w-0 flex-1">
                    {inProgress ? (
                      <span
                        aria-disabled="true"
                        title={t('library.opensWhenReady', {
                          stage: indexingStageLabel(doc.indexingStatus),
                        })}
                        className="cursor-not-allowed font-serif text-base leading-snug text-muted-foreground"
                        data-testid={`knowledge-list-link-${doc.token}`}
                      >
                        {documentTitle(doc)}
                      </span>
                    ) : onOpen ? (
                      <button
                        type="button"
                        className="text-left font-serif text-base leading-snug hover:underline"
                        data-testid={`knowledge-list-link-${doc.token}`}
                        onClick={() => onOpen(doc.token)}
                      >
                        {documentTitle(doc)}
                      </button>
                    ) : (
                      <Link
                        href={`/knowledge/${doc.token}`}
                        className="font-serif text-base leading-snug hover:underline"
                        data-testid={`knowledge-list-link-${doc.token}`}
                      >
                        {documentTitle(doc)}
                      </Link>
                    )}
                    {description ? (
                      <Text size="xs" color="muted" className="mt-0.5 line-clamp-1">
                        {description}
                      </Text>
                    ) : null}
                    <Text size="xs" color="muted" className="mt-0.5 sm:hidden">
                      {[
                        fileKindLabel(doc),
                        market ? marketDisplayName(market) : null,
                        year,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                    {isReady ? null : (
                      <div className="mt-1.5">
                        <KnowledgeIndexingBadge status={doc.indexingStatus} />
                      </div>
                    )}
                    <KnowledgeStageFailureNotice
                      doc={doc}
                      variant="compact"
                      className="mt-1.5"
                      onDocumentUpdated={onDocumentUpdated}
                    />
                  </div>
                </div>
              </td>
              <td className="hidden px-4 py-3 text-muted-foreground sm:table-cell">
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  {fileKindLabel(doc)}
                  {doc.isSimulationArtifact ? (
                    <span
                      className="rounded-full bg-muted px-1.5 py-0.5 text-[10px]"
                      data-testid="knowledge-list-sim-badge"
                    >
                      {t('library.simBadge')}
                    </span>
                  ) : null}
                  {doc.isScannedPdf ? (
                    <span
                      className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-800 dark:text-amber-300"
                      data-testid="knowledge-list-scanned-badge"
                    >
                      {t('library.scanned')}
                    </span>
                  ) : null}
                </span>
              </td>
              <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">
                {market ? (
                  <MarketLabel
                    label={marketDisplayName(market)}
                    code={marketFlagCode(market)}
                    data-testid={`knowledge-list-market-${doc.token}`}
                  />
                ) : null}
              </td>
              <td
                className="hidden px-4 py-3 tabular-nums text-muted-foreground md:table-cell"
                data-testid={`knowledge-list-year-${doc.token}`}
              >
                {year}
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
